import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import Slider, { CustomArrowProps } from 'react-slick';

import { SizeData } from '../../../data/model';
import { ProductModelSelectorTranslation } from '../../../localization/SiteTranslation';
import Carousel, { CarouselProps } from '../../../shared/components/Carousel/Carousel';
import {
  SliderNextArrow,
  SliderPrevArrow,
} from '../../../shared/components/SliderNavigationArrows/SliderNavigationArrows';
import Heading from '../../../shared/components/Typography/Heading/Heading';
import { VisualizationAspectRatio } from '../../../shared/visualization';
import css from './ProductModelSelector.css';
import ProductModelSelectorItem from './ProductModelSelectorItem/ProductModelSelectorItem';
import ProductModelSelectorState from './ProductModelSelectorState';

interface ProductModelSelectorProps {
  className?: string;
  state: ProductModelSelectorState;
  translation: ProductModelSelectorTranslation;
}

const CustomPrevArrow = (props: CustomArrowProps) => {
  return (
    <SliderPrevArrow
      className={classNames(props.className, css.arrowPrev)}
      iconClassName={css.icon}
      onClick={props.onClick}
    />
  );
};

const CustomNextArrow = (props: CustomArrowProps) => {
  return (
    <SliderNextArrow
      className={classNames(props.className, css.arrowNext)}
      iconClassName={css.icon}
      onClick={props.onClick}
    />
  );
};

const getImageWidth = (
  targetHeight: number,
  fallbackWidth: number,
  aspectRation: string,
  originalImageSize?: SizeData,
) => {
  if (!originalImageSize) {
    return fallbackWidth * (aspectRation === VisualizationAspectRatio.Default ? 1 : 2);
  }

  const targetWidth = (targetHeight * originalImageSize.width) / originalImageSize.height;
  return targetWidth;
};

const ProductModelSelector = observer(function ({ state, className, translation }: ProductModelSelectorProps) {
  const sliderSettings: CarouselProps = {
    speed: 300,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    variableWidth: true,
  };

  const sliderRef = useRef<Slider>();

  const handleClick = (id: string) => {
    state.selectItem(id);
    sliderRef.current.slickGoTo(state.activeIndex);
  };

  return (
    <div className={className}>
      <Heading level={2} className={css.heading}>
        {translation.heading}
      </Heading>
      <Carousel ref={sliderRef} className={css.carousel} {...sliderSettings} initialSlide={state.activeIndex}>
        {state.items.map((item) => {
          const imageHeight = 170;
          const imageWidth = getImageWidth(
            imageHeight,
            120,
            item.data.visualizationAspectRatio,
            item.data.outline?.size,
          );
          const margin = 28.5; // Just to keep backward compatibility and have the same card size as before
          return (
            <ProductModelSelectorItem
              key={item.data.id}
              style={{ width: imageWidth + margin }}
              item={item}
              handleClick={() => handleClick(item.data.id)}
              width={imageWidth}
              height={imageHeight}
            />
          );
        })}
      </Carousel>
    </div>
  );
});

export default ProductModelSelector;
